import { Injectable } from '@angular/core';
import type { MAEWStatus, TimeSlot } from '../models/job.model';

/**
 * Result of classifying an attempt's date/time into a MAEW slot.
 *
 * `primary` is the time-of-day letter (M/A/E); `isWeekend` is `true` on
 * Saturday or Sunday. The two together — plus how many weekend attempts the
 * job already carries — select the credited slots, see {@link creditedSlotKeys}.
 */
export interface SlotClassification {
  /** Time-of-day slot: `'M'` 00:00–11:59, `'A'` 12:00–16:59, `'E'` 17:00–23:59. */
  primary: 'M' | 'A' | 'E';
  /** `true` on Saturday (day=6) or Sunday (day=0). */
  isWeekend: boolean;
}

/** The four MAEW slot keys, in display order. */
const MAEW_KEYS = ['M', 'A', 'E', 'W'] as const;

/** Weekend attempt token for each time-of-day slot (`Wm`/`Wa`/`We`). */
const WEEKEND_TOKEN: Record<'M' | 'A' | 'E', 'Wm' | 'Wa' | 'We'> = {
  M: 'Wm',
  A: 'Wa',
  E: 'We',
};

/**
 * Maps a {@link MAEWStatus} key to its {@link TimeSlot} label.
 *
 * @remarks
 * The inverse of the `maewKey` map in `JobsService.recordAttempt()`, which
 * takes a `TimeSlot` and sets the matching flag.
 */
export const SLOT_LABEL: Record<keyof MAEWStatus, TimeSlot> = {
  M: 'Morning',
  A: 'Afternoon',
  E: 'Evening',
  W: 'Weekend',
};

/**
 * The attempt token the SERVER would record for this attempt — the client-side
 * mirror of the `attemptflags` insert trigger.
 *
 * @remarks
 * The trigger is the authority on what an attempt is worth; this function
 * reproduces its decision so the app's optimistic local credit, its
 * final-attempt prediction and the flags that come back on the next sync all
 * agree. Its logic, in the trigger's own order:
 *
 * 1. A **weekday** attempt is always its bare time-of-day letter (`M`/`A`/`E`).
 * 2. A **weekend** attempt (Sat/Sun) starts as `Wm`/`Wa`/`We` by time of day.
 * 3. **But** if that exact weekend token has already been recorded for this job
 *    AND its weekday equivalent has NOT, the attempt is recorded as the
 *    **equivalent letter instead** (`Wm` → `M`). So a repeat of the *same*
 *    weekend slot credits the matching weekday slot.
 * 4. If the equivalent is already recorded too, it falls through and records
 *    another weekend token (which changes nothing visible — `W` is set).
 *
 * **The substitution is per weekend sub-slot, not a global "2nd weekend
 * attempt" rule.** `Wm` then `Wa` records `Wm`,`Wa` → `W` only, because `Wa`
 * had not been seen before; `Wm` then `Wm` records `Wm`,`M` → `W` and `M`.
 *
 * The trigger's `instr(binary flags, binary @per)` substring test is an exact
 * `includes()` here because {@link parseAttemptCodes} has already tokenised the
 * string. Binary comparison is what stops `'M'` matching the lowercase `m` of
 * `'Wm'`; comparing whole tokens is equivalent and needs no case ceremony.
 *
 * @param slot - Classification of the attempt's date/time.
 * @param existingCodes - Tokens already recorded for this job, oldest first
 *   (`Job.attemptCodes`).
 * @returns The token this attempt records (`M`/`A`/`E`/`Wm`/`Wa`/`We`).
 */
export function attemptTokenFor(
  slot: SlotClassification,
  existingCodes: readonly string[],
): string {
  if (!slot.isWeekend) return slot.primary;

  const weekendToken = WEEKEND_TOKEN[slot.primary];
  const equivalent   = slot.primary;

  const repeatOfSameWeekendSlot = existingCodes.includes(weekendToken);
  const equivalentNotYetRecorded = !existingCodes.includes(equivalent);

  return repeatOfSameWeekendSlot && equivalentNotYetRecorded ? equivalent : weekendToken;
}

/**
 * The MAEW slot a recorded token counts toward.
 *
 * @remarks
 * Every weekend token — `Wm`/`Wa`/`We`, and a bare `W` from older data —
 * counts toward `W` and never toward its own time-of-day letter. A time-of-day
 * letter is only ever credited by a bare `M`/`A`/`E` token, which is exactly
 * what {@link attemptTokenFor} records for a repeat weekend attempt.
 *
 * @param token - A token from `timeofday` / {@link attemptTokenFor}.
 */
export function slotKeyForToken(token: string): keyof MAEWStatus {
  return token.startsWith('W') ? 'W' : (token as keyof MAEWStatus);
}

/**
 * The single MAEW slot this attempt credits.
 *
 * @remarks
 * **One attempt credits exactly ONE slot**, matching the trigger, which writes
 * exactly one token per attempt. A weekend attempt whose slot repeats credits
 * its weekday equivalent *instead of* `W` (not in addition to it) — `W` is
 * already set by the earlier attempt that first recorded that weekend slot.
 *
 * **This is slot *selection*, not eligibility.** Nothing here gates *whether*
 * a final attempt may occur; per MOB-298 the final attempt is simply the one
 * that covers the 4th slot, in any order, on any day.
 *
 * @param slot - Classification of the attempt's date/time.
 * @param existingCodes - Tokens already recorded for this job (`Job.attemptCodes`).
 */
export function creditedSlotKey(
  slot: SlotClassification,
  existingCodes: readonly string[],
): keyof MAEWStatus {
  return slotKeyForToken(attemptTokenFor(slot, existingCodes));
}

/**
 * `true` when logging an attempt in `slot` would leave all four MAEW slots
 * covered — i.e. this is the Final Attempt.
 *
 * @remarks
 * The MOB-298 rule: the four slots must be covered **in any order, on any
 * day**. There is deliberately no weekend gate (an earlier revision required
 * the closing attempt to be a Weekend one; that was removed).
 *
 * Since the attempt credits exactly one slot ({@link creditedSlotKey}),
 * covering all four genuinely requires four separate attempts — but note that
 * a repeat weekend attempt can be the one that covers a *weekday* slot, so the
 * closing attempt need not fall on a weekday to cover `M`/`A`/`E`.
 *
 * @param maew - The job's MAEW flags **before** this attempt is recorded.
 * @param slot - Classification of the attempt's date/time.
 * @param existingCodes - Tokens already recorded for this job (`Job.attemptCodes`).
 * @returns `true` when every slot is either already flagged or credited now.
 */
export function wouldExhaustMaew(
  maew: MAEWStatus,
  slot: SlotClassification,
  existingCodes: readonly string[],
): boolean {
  const credited = creditedSlotKey(slot, existingCodes);
  return MAEW_KEYS.every(key => maew[key] || key === credited);
}

/**
 * Splits a concatenated `timeofday` string into individual attempt tokens.
 *
 * @remarks
 * The server appends one token per attempt, so the string is a chronological
 * log (e.g. `"MWmEWeA"`). Two-char weekend variants (`Wm`, `Wa`, `We`) are
 * matched before single-char codes so `W` never greedily consumes the first
 * character of a two-char token.
 *
 * Lives here rather than in `job.adapter.ts` because {@link attemptTokenFor}
 * consumes the tokens it produces — the tokenisation and the rule that reads
 * the tokens belong together.
 *
 * @param timeofday - Raw `timeofday` value from the API.
 * @returns Recognised tokens, oldest first; empty when none match.
 */
export function parseAttemptCodes(timeofday: string): string[] {
  return timeofday.match(/Wm|We|Wa|M|E|A|W/g) ?? [];
}

/**
 * Derives MAEW attempt flags from a token list.
 *
 * @remarks
 * Pure presence: each bare `M`/`A`/`E` sets its own slot, and every weekend
 * token sets `W` only. The weekend-repeat rule needs no replay here — the
 * server has already applied it at write time by recording a bare letter
 * ({@link attemptTokenFor}), so the flags follow from the tokens directly.
 *
 * @param codes - Tokens from {@link parseAttemptCodes}.
 */
export function maewFromCodes(codes: readonly string[]): MAEWStatus {
  const maew: MAEWStatus = { M: false, A: false, E: false, W: false };
  for (const code of codes) maew[slotKeyForToken(code)] = true;
  return maew;
}

/**
 * Derives the MAEW slot from the device's local time.
 *
 * @remarks
 * Slot rules match the prototype (`hr < 12 ? 'Morning' : hr < 17 ? 'Afternoon' : 'Evening'`)
 * and the server trigger's own boundaries (`< 12:00:00`, `< 17:00:00`, `<= 23:59:59`):
 * - **M** — 00:00–11:59 (Morning, including overnight/early hours)
 * - **A** — 12:00–16:59 (Afternoon)
 * - **E** — 17:00–23:59 (Evening)
 *
 * `isWeekend` is `true` on Saturday/Sunday (the trigger's `dayofweek in (1,7)`).
 * On a weekend it selects a `Wm`/`Wa`/`We` token rather than the bare letter;
 * `primary` still matters there, because it names both which weekend token is
 * recorded and which weekday slot a repeat of that token credits — see
 * {@link attemptTokenFor}.
 */
@Injectable({ providedIn: 'root' })
export class MaewService {
  /**
   * Derives the MAEW slot classification from `now`.
   *
   * @param now - The current time (defaults to `new Date()` — injectable for tests).
   * @returns A {@link SlotClassification} with `primary` and `isWeekend`.
   */
  classifySlot(now = new Date()): SlotClassification {
    const hour      = now.getHours();
    const day       = now.getDay();
    const isWeekend = day === 0 || day === 6;
    const primary   = hour < 12 ? 'M'
                    : hour < 17 ? 'A'
                    :             'E';
    return { primary, isWeekend };
  }
}
